// components/received/AcknowledgmentModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';

const AcknowledgmentModal = ({ show, onSuccess, onError, inheritance, endpoints }) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDecline, setShowConfirmDecline] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  
  // Reset state when modal is shown
  React.useEffect(() => {
    if (show) {
      setTermsAccepted(false);
      setShowError(false);
      setShowConfirmDecline(false);
      setConfirmationText('');
    }
  }, [show]);
  
  // Handle acknowledge submission
  const handleAcknowledge = async (e) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      setShowError(true);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get recipient info from session storage
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const username = recipientInfo.username;
      
      if (!username || !inheritance?.id) {
        throw new Error('Missing required information');
      }
      
      // Get the specific token for this inheritance if available
      const inheritanceToken = inheritance?.token || 
                              sessionStorage.getItem(`token_${inheritance.id}`) || 
                              sessionStorage.getItem('token');
      
      const response = await fetch(endpoints.ACKNOWLEDGE_INHERITANCE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${inheritanceToken}`
        },
        body: JSON.stringify({
          username,
          inheritanceId: inheritance.id,
          termsAccepted: true
        }),
      });
      
      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (data.error) {
          onError(data.error);
        } else {
          onSuccess();
        }
      } else {
        // Not JSON, log the text for debugging
        const text = await response.text();
        console.error('Non-JSON response:', text);
        onError('Unexpected response from server');
      }
    } catch (error) {
      onError(`Acknowledgment failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle decline button click
  const handleDeclineClick = () => {
    setShowConfirmDecline(true);
  };
  
  // Handle decline confirm
  const handleDeclineConfirm = async () => {
    const expectedText = 'I understand that I will lose access to this Endowment';
    
    if (confirmationText !== expectedText) {
      onError('Please type the exact confirmation text to proceed');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get recipient info from session storage
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const username = recipientInfo.username;
      
      if (!username || !inheritance?.id) {
        throw new Error('Missing required information');
      }
      
      const response = await fetch(endpoints.REJECT_INHERITANCE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          inheritanceId: inheritance.id,
          confirmationText: expectedText
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShowConfirmDecline(false);
        onSuccess();
      } else {
        onError(data.error || 'Failed to decline Endowment');
      }
    } catch (error) {
      onError(`Error declining inheritance: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      size="lg"
      centered
      className="re-acknowledgment-modal"
    >
      <Modal.Header>
        <Modal.Title>
          {showConfirmDecline ? 
            '⚠️ Warning: Decline Endowment' : 
            'Acknowledge Endowment'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {!showConfirmDecline ? (
          // Acknowledgment Form
          <Form onSubmit={handleAcknowledge}>
            <div className="re-terms-container text-white mb-4">
              <h6 className="mb-4 text-center fw-bold">Terms & Conditions</h6>
              
              <ol className="re-terms-list">
                <li className="mb-3">
                  You have been set up to receive a endowment file on a date established by the sender
                </li>
                
                <li className="mb-3">
                  You acknowledge this endowment set up by accepting these Terms & Conditions
                </li>
                
                <li className="mb-3">
                  When the established date expires, the files set up in the sender endowment Vault
                  will be available to transfer to you
                </li>
                
                <li className="mb-3">
                  You will be notified by email at the time the endowment files will be available for
                  you to accept
                </li>
                
                <li className="mb-3">
                  You will have 30 days from the date of notification to access PhēnixLoc using your
                  username and password just now established, and to download/view the Endowment files
                  that the sender makes available
                </li>
                
                <li className="mb-3">
                  Keep the username and password you just set up in a safe place in order to access
                  the endowment files when they are made available!
                </li>
              </ol>
            </div>
            
            <Form.Group className="mb-3">
              <div className="re-custom-checkbox">
                <Form.Check
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) {
                      setShowError(false);
                    }
                  }}
                  label="I have read and agree to the Terms & Conditions"
                  className="text-white re-terms-checkbox"
                />
              </div>
            </Form.Group>
            
            {showError && (
              <Alert variant="danger" className="mb-3">
                Please accept the Terms & Conditions to continue.
              </Alert>
            )}
            
            <p className="fw-bold text-white mb-4">
              Do you acknowledge receipt of this Endowment and agree to the Terms & Conditions?
            </p>
            
            <div className="re-action-buttons">
              <Button
                type="button"
                variant="danger"
                className="re-btn-decline"
                onClick={handleDeclineClick}
                disabled={isLoading}
              >
                <i className="fas fa-times-circle me-2"></i> Decline
              </Button>
              
              <Button
                type="submit"
                variant="success"
                className="re-btn-acknowledge"
                disabled={!termsAccepted || isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle me-2"></i> Acknowledge and Continue
                  </>
                )}
              </Button>
            </div>
          </Form>
        ) : (
          // Decline Confirmation Form
          <div>
            <Alert variant="warning">
              <strong>Important:</strong> By declining this Endowment, you will permanently lose access to all its contents.
            </Alert>
            
            <p>To confirm, please type:</p>
            <p className="re-confirmation-text">
              "I understand that I will lose access to this Endowment"
            </p>
            
            <Form.Group className="mb-4">
              <Form.Control
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type the confirmation text here"
                isInvalid={confirmationText !== '' && confirmationText !== 'I understand that I will lose access to this Endowment'}
                disabled={isLoading}
              />
              <Form.Control.Feedback type="invalid">
                Please type the exact confirmation text to proceed.
              </Form.Control.Feedback>
            </Form.Group>
            
            <div className="re-action-buttons">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowConfirmDecline(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              
              <Button
                type="button"
                variant="danger"
                onClick={handleDeclineConfirm}
                disabled={confirmationText !== 'I understand that I will lose access to this Endowment' || isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processing...
                  </>
                ) : (
                  'Decline Endowment'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

AcknowledgmentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  inheritance: PropTypes.object,
  endpoints: PropTypes.object.isRequired
};

export default AcknowledgmentModal;